/**
 * Chat intégré dans la table brouillon
 *
 * Style aligné sur les discussions de groupe :
 * - Messages de l'utilisateur à droite (bulle verte)
 * - Messages des autres à gauche (bulle grise)
 * - Messages système centrés
 * - Avatars des joueurs
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { DraftMessage } from '../types/draft';
import { PlayerAvatar } from './PlayerAvatar';

interface DraftTableChatProps {
  messages: DraftMessage[];
  currentPseudo: string;
  onSendMessage: (text: string) => void;
}

export default function DraftTableChat({
  messages,
  currentPseudo,
  onSendMessage,
}: DraftTableChatProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    onSendMessage(text);
    setInput('');
    inputRef.current?.focus();
  }, [input, onSendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const formatTime = (timestamp: number) =>
    new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  const isOwn = (pseudo: string) =>
    pseudo.toLowerCase() === currentPseudo.toLowerCase();

  // Group consecutive messages from same author (non-system)
  // to avoid repeating the avatar + name
  const shouldShowAuthor = (msg: DraftMessage, idx: number): boolean => {
    if (msg.isSystem) return false;
    if (idx === 0) return true;
    const prev = messages[idx - 1];
    if (prev.isSystem) return true;
    return prev.pseudo.toLowerCase() !== msg.pseudo.toLowerCase();
  };

  return (
    <div className="draft-chat">
      <div className="draft-chat-messages">
        {messages.map((msg, idx) => {
          if (msg.isSystem) {
            return (
              <div key={msg.id} className="draft-chat-msg draft-chat-msg--system">
                <div className="draft-chat-system-pill">
                  <span className="draft-chat-system-text">{msg.text}</span>
                  <span className="draft-chat-time">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            );
          }

          const own = isOwn(msg.pseudo);
          const showAuthor = shouldShowAuthor(msg, idx);

          return (
            <div
              key={msg.id}
              className={`draft-chat-msg ${own ? 'draft-chat-msg--own' : 'draft-chat-msg--other'}`}
            >
              {/* Avatar column */}
              {!own && (
                <div className="draft-chat-avatar-col">
                  {showAuthor ? (
                    <PlayerAvatar pseudo={msg.pseudo} size={28} />
                  ) : (
                    <div className="draft-chat-avatar-spacer" />
                  )}
                </div>
              )}

              <div className="draft-chat-bubble-col">
                {showAuthor && !own && (
                  <div className="draft-chat-author">{msg.pseudoDisplay}</div>
                )}
                <div className="draft-chat-bubble">
                  <span className="draft-chat-text">{msg.text}</span>
                  <span className="draft-chat-meta">{formatTime(msg.timestamp)}</span>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="draft-chat-input-area">
        <input
          ref={inputRef}
          type="text"
          className="draft-chat-input"
          placeholder="Écrivez un message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={500}
        />
        <button
          className="draft-chat-send-btn"
          onClick={handleSend}
          disabled={!input.trim()}
        >
          ➤
        </button>
      </div>
    </div>
  );
}
